import { percentOf, planProgress, testStatus, type TestPlan } from "./test-plan";

export type ScoredTest = {
  id: string;
  name: string;
  testDate: string;
  obtained: number;
  total: number;
  percent: number;
  physics: number | null;
  chemistry: number | null;
  maths: number | null;
};

export const SUBJECT_KEYS = ["physics", "chemistry", "maths"] as const;
export type SubjectKey = (typeof SUBJECT_KEYS)[number];
export const SUBJECT_LABEL: Record<SubjectKey, string> = {
  physics: "Physics",
  chemistry: "Chemistry",
  maths: "Mathematics",
};

/** Every test that has a saved score, oldest first. */
export function scoredTests(plans: TestPlan[]): ScoredTest[] {
  return plans
    .filter((p) => p.score)
    .map((p) => ({
      id: p.id,
      name: p.name,
      testDate: p.testDate,
      obtained: p.score!.obtained,
      total: p.score!.total,
      percent: percentOf(p.score!.obtained, p.score!.total),
      physics: p.score!.physics,
      chemistry: p.score!.chemistry,
      maths: p.score!.maths,
    }))
    .sort((a, b) => a.testDate.localeCompare(b.testDate));
}

const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
const round = (n: number) => Math.round(n * 10) / 10;

export type SubjectStat = { key: SubjectKey; label: string; average: number; percent: number };

export type Performance = {
  attempted: number;
  missed: number;
  averageScore: number;
  highest: ScoredTest | null;
  lowest: ScoredTest | null;
  averagePercent: number;
  improvement: number;
  streak: number;
  best: ScoredTest | null;
  subjects: SubjectStat[];
  strongest: SubjectStat | null;
  weakest: SubjectStat | null;
  history: ScoredTest[];
  weeklyTrend: number;
  monthlyTrend: number;
};

/** How many of the most recent tests improved on (or matched) the one before. */
function improvingStreak(list: ScoredTest[]): number {
  let streak = 0;
  for (let i = list.length - 1; i > 0; i -= 1) {
    if (list[i].percent >= list[i - 1].percent) streak += 1;
    else break;
  }
  return streak;
}

function trendWithin(list: ScoredTest[], today: string, days: number): number {
  const cutoff = new Date(`${today}T00:00:00Z`).getTime() - days * 86400000;
  const recent = list.filter((t) => new Date(`${t.testDate}T00:00:00Z`).getTime() >= cutoff);
  const older = list.filter((t) => new Date(`${t.testDate}T00:00:00Z`).getTime() < cutoff);
  if (!recent.length || !older.length) return 0;
  return round(avg(recent.map((t) => t.percent)) - avg(older.map((t) => t.percent)));
}

export function computePerformance(plans: TestPlan[], today: string): Performance {
  const list = scoredTests(plans);
  const missed = plans.filter((p) => testStatus(p, today) === "missed").length;

  const subjects: SubjectStat[] = SUBJECT_KEYS.map((key) => {
    const rows = list.filter((t) => t[key] !== null);
    const average = round(avg(rows.map((t) => t[key] as number)));
    const percent = round(
      avg(rows.map((t) => ((t[key] as number) / (t.total / 3 || 1)) * 100)),
    );
    return { key, label: SUBJECT_LABEL[key], average, percent };
  }).filter((s) => s.average > 0 || s.percent > 0);

  const ranked = [...subjects].sort((a, b) => b.percent - a.percent);
  const sortedByScore = [...list].sort((a, b) => a.percent - b.percent);

  const first = list[0];
  const last = list[list.length - 1];
  const improvement =
    list.length > 1 && first.percent > 0 ? round(last.percent - first.percent) : 0;

  return {
    attempted: list.length,
    missed,
    averageScore: round(avg(list.map((t) => t.obtained))),
    highest: sortedByScore[sortedByScore.length - 1] ?? null,
    lowest: sortedByScore[0] ?? null,
    averagePercent: round(avg(list.map((t) => t.percent))),
    improvement,
    streak: improvingStreak(list),
    best: sortedByScore[sortedByScore.length - 1] ?? null,
    subjects,
    strongest: ranked[0] ?? null,
    weakest: ranked.length > 1 ? ranked[ranked.length - 1] : null,
    history: [...list].reverse(),
    weeklyTrend: trendWithin(list, today, 7),
    monthlyTrend: trendWithin(list, today, 30),
  };
}

export type Insight = { label: string; body: string };

/** Rule-based coaching notes derived from the saved scores and task progress. */
export function buildInsights(plans: TestPlan[], today: string): Insight[] {
  const perf = computePerformance(plans, today);
  if (!perf.attempted) return [];
  const last = perf.history[0];
  const insights: Insight[] = [];

  if (perf.strongest) {
    insights.push({
      label: "Biggest strength",
      body: `${perf.strongest.label} is carrying your score — averaging ${perf.strongest.average} marks per test.`,
    });
  }
  if (perf.weakest) {
    insights.push({
      label: "Biggest weakness",
      body: `${perf.weakest.label} is your lowest scoring subject, averaging ${perf.weakest.average} marks.`,
    });
    insights.push({
      label: "Needs immediate attention",
      body: `Give ${perf.weakest.label} the first slot of every study day this week.`,
    });
    insights.push({
      label: "Revision priority",
      body: `Re-revise your weak ${perf.weakest.label} chapters before touching new theory.`,
    });
    insights.push({
      label: "Practice priority",
      body: `Target 40–60 ${perf.weakest.label} questions daily until the next test.`,
    });
  }

  const projected = Math.max(
    0,
    Math.min(100, round(last.percent + (perf.monthlyTrend || perf.improvement / 2))),
  );
  insights.push({
    label: "If this trend continues",
    body: `Your next test lands near ${projected}% based on your recent curve.`,
  });

  const pending = plans
    .filter((p) => testStatus(p, today) !== "completed")
    .map((p) => planProgress(p).percent);
  const prep = pending.length ? Math.round(avg(pending)) : 100;
  insights.push({
    label: "Motivation",
    body:
      perf.improvement >= 0
        ? `You're trending up — ${prep}% of your current prep plan is done. Keep the streak alive.`
        : `One dip doesn't define you. Finish the remaining ${100 - prep}% of your plan and the score follows.`,
  });

  return insights;
}